
import type { PropType } from 'vue'
import type { ColorEnum } from '../enums'
import { ResizeObserver as ResizeObserverPolyfill } from '@juggle/resize-observer'

import { defineComponent } from 'vue'

import TransitionSize from './transition-size.component.vue'

export interface SegmentedBarDataItem {
  id: string
  label: string
  value: number
  color: ColorEnum
  children?: SegmentedBarDataItem[]
}

export default defineComponent({
  name: 'SegmentedBar',
  components: {
    TransitionSize,
  },
  props: {
    data: {
      type: Array as PropType<Array<SegmentedBarDataItem>>,
      required: true,
    },
    selectedDataItem: {
      type: Object as PropType<SegmentedBarDataItem>,
    },
    hoveredDataItem: {
      type: Object as PropType<SegmentedBarDataItem>,
    },
    labelsPosition: {
      type: String, // 'top', 'bottom'
      default: 'top',
    },
  },
  data() {
    return {
      baseClass: 'epb-segmented-bar',
      resizeObserver: undefined,
      mutationObserver: undefined,
    }
  },
  computed: {
    classes(): (string | Record<string, boolean>)[] {
      return [
        this.baseClass,
        `${this.baseClass}--${this.labelsPosition}`,
        {
          [`${this.baseClass}--hovered`]: !!this.hoveredDataItem,
          [`${this.baseClass}--parent`]: this.childrenVisible,
        },
      ]
    },
    totalValue(): number {
      return this.data.reduce(
        (total: number, dataItem: SegmentedBarDataItem) => dataItem.value + total,
        0,
      )
    },
    childrenVisible(): boolean {
      return !!this.selectedDataItem && !!this.selectedDataItem.children
    },
  },
  mounted(): void {
    const ResizeObserver = window.ResizeObserver || ResizeObserverPolyfill
    this.resizeObserver = new ResizeObserver(entries => {
      entries.forEach(entry => {
        this.updateSegmentLabelClasses(entry.target as HTMLElement)
      })
    })
    this.mutationObserver = new MutationObserver((mutations: MutationRecord[]) => {
      mutations.forEach(() => {
        this.$refs.segment.forEach((segment: HTMLElement) => {
          this.resizeObserver.observe(segment)
        })
      })
    })
    this.mutationObserver.observe(this.$el, { attributes: true, childList: true, subtree: true })
  },
  beforeDestroy(): void {
    this.resizeObserver.disconnect()
    this.mutationObserver.disconnect()
  },
  methods: {
    getSegmentClasses(dataItem: SegmentedBarDataItem): (string | Record<string, boolean>)[] {
      const baseClass = `${this.baseClass}__segment`
      return [
        baseClass,
        {
          [`${baseClass}--hovered`]: dataItem === this.hoveredDataItem,
        },
      ]
    },
    getSegmentStyle(dataItem: SegmentedBarDataItem): Record<string, any> {
      let width = (dataItem.value / this.totalValue) * 100
      if (this.selectedDataItem) {
        width = dataItem === this.selectedDataItem ? 100 : 0
      }
      return {
        width: `${width}%`,
        ...(!width && { marginLeft: 0, marginRight: 0, opacity: 0 }),
      }
    },
    getSegmentBarColor(dataItem: SegmentedBarDataItem): ColorEnum {
      return dataItem.color
    },
    updateSegmentLabelClasses(segmentEl: HTMLElement): void {
      const labelCls = `${this.baseClass}__segment-label`
      const labelEl = segmentEl.getElementsByClassName(labelCls)[0] as HTMLElement

      const { x: segmentX, width: segmentWidth } = segmentEl.getBoundingClientRect()
      const { width: labelWidth } = labelEl.getBoundingClientRect()
      const { x: rootX, width: rootWidth } = this.$el.getBoundingClientRect()

      labelEl.classList.toggle(`${labelCls}--hidden`, segmentWidth < labelWidth)
      labelEl.classList.toggle(`${labelCls}--end`, segmentX - rootX + labelWidth > rootWidth)
    },
  },
})
